import { useRef } from "react";

import { setLocationStatus } from "../redux/slices/applicationSlice";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useGetMapConfigQuery } from "../redux/services/vectormapsAPI";
import { LatLng } from "../redux/services/types";

import { initialisePositionLogging } from "../utils/position-logging/initialisePostionLogging";
import { initialiseGeolocationDebugger } from "../utils/geolocation-debugging/initialise-geolocation-debugger";
import { LMPositionCallback } from "../utils/geolocation-debugging/MockGeolocationProvider";
import { UUID } from "../utils/position-logging/positionLoggingAPI";

import { LocationStatus } from "../components/LocationButton/LocationButton";
import GeofencePlugin from "../components/Map/plugins/geofence-control";
import PositionPlugin from "../components/Map/plugins/position-control";
import FloorControl from "../components/Map/plugins/floor-control";

enum LocationPermissionStatus {
  GRANTED = "granted",
  DENIED = "denied",
  PROMPT = "prompt",
}

export default function useLocationPermission({
  project,
  floorControl,
  positionControl,
  geofenceControl,
}: {
  project: string;
  floorControl: FloorControl | null;
  positionControl: PositionPlugin | null;
  geofenceControl: GeofencePlugin | null;
}) {
  const dispatch = useAppDispatch();
  const { queryParamsConfig } = useAppSelector((state) => state.application);

  const positionLoggingEnabled = queryParamsConfig.positionLogging === "enable";

  const positionLoggingSessionUUID =
    queryParamsConfig.positionLoggingSessionUUID &&
    queryParamsConfig.positionLoggingSessionUUID?.length > 0
      ? (queryParamsConfig.positionLoggingSessionUUID as UUID)
      : null;

  const { positionLogger, positionLoggingGeolocationProvider } =
    initialisePositionLogging(
      project,
      positionLoggingEnabled,
      positionLoggingSessionUUID,
    );

  const setLocationMarker = ({
    latitude,
    longitude,
    bearing,
    altitude,
    accuracy,
    floorId,
  }: {
    latitude: number;
    longitude: number;
    bearing: number | null;
    altitude: number | null;
    accuracy: number;
    floorId: string | null;
  }) => {
    var outFloorId = null;
    var outFloor = null;
    if (floorControl && positionControl) {
      const floors = floorControl.getAllFloors();
      if (floorId !== null) {
        const selectedFloor = floors!.filter(
          (floor) => floor.short_name === floorId.toLocaleUpperCase(),
        );
        if (selectedFloor.length > 0) {
          outFloorId = selectedFloor[0].id;
          outFloor = selectedFloor[0].floor;
        }
      }

      positionControl.setMarker({
        latitude,
        longitude,
        floorID: outFloorId,
        floor: outFloor,
        bearing,
        altitude,
        accuracy,
        ts: new Date(),
      });
    }
  };

  const handleUserWithinGeofence = ({ latitude, longitude }: LatLng) => {
    if (geofenceControl)
      geofenceControl.handleUserLocationForGeofences({
        latitude,
        longitude,
      });
  };

  const mockGeolocationCallback = () => {};

  const onPermissionDenied = () => {
    if (positionControl) positionControl.stopStabilisation();
  };

  const geolocationMock = queryParamsConfig.consoleLocation
    ? initialiseGeolocationDebugger(mockGeolocationCallback)
    : null;

  // If we have a geolocation mock provider or a position logging geolocation provider,
  // use them instead of the browser geolocation.
  const geolocation: Geolocation =
    geolocationMock ||
    positionLoggingGeolocationProvider ||
    navigator.geolocation;

  const watchId = useRef<number | null>(null);

  const { data: mapData } = useGetMapConfigQuery();

  const clearUserLocationWatch = () => {
    if (geolocation && watchId.current !== null) {
      geolocation.clearWatch(watchId.current);
    }
  };

  const requestUserLocation = () => {
    // if we are in console location mode, we don't need to show the searching status as we are setting the location manually
    if (!queryParamsConfig.consoleLocation)
      dispatch(setLocationStatus(LocationStatus.SEARCHING));

    if (geolocation) {
      watchId.current = geolocation.watchPosition(
        handleUserLocationSuccess,
        handleUserLocationFailed,
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
      );
    }
  };

  const handlePermissions = (state: PermissionState) => {
    switch (state) {
      case LocationPermissionStatus.PROMPT:
        requestUserLocation();
        break;
      case LocationPermissionStatus.GRANTED:
        requestUserLocation();
        break;
      case LocationPermissionStatus.DENIED: // this state seems to only happen on chrome
        clearUserLocationWatch();
        onPermissionDenied?.();
        dispatch(setLocationStatus(LocationStatus.DISABLED));
        return; // Early return to prevent any further location requests
      default:
        break;
    }
  };

  const fallbackGeolocationPermissionCheck = () => {
    if (!geolocation) {
      console.error("Geolocation is not supported by your browser.");
      return;
    }

    geolocation.getCurrentPosition(
      () => {
        handlePermissions("granted");
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          clearUserLocationWatch();
          onPermissionDenied?.();
          handlePermissions("denied");
          return; // Early return to prevent any further processing
        } else {
          console.error("Geolocation error:", error);
        }
      },
    );
  };

  const handleLocationRequest = async () => {
    try {
      if ("permissions" in navigator && "query" in navigator.permissions) {
        const { state } = await navigator.permissions.query({
          name: "geolocation",
        });
        handlePermissions(state);
      } else {
        fallbackGeolocationPermissionCheck();
      }
    } catch (error) {
      fallbackGeolocationPermissionCheck();
    }
  };

  const handleUserLocationSuccess: LMPositionCallback = (position, floorId) => {
    const {
      coords: { latitude, longitude, heading, altitude, accuracy },
    } = position;
    const bearing = heading;
    const floor = floorId ? floorId : null;

    positionLogger && positionLogger.log(position, floor);

    if (
      watchId &&
      mapData &&
      (longitude < mapData.extents.bottom_left.longitude ||
        longitude > mapData.extents.top_right.longitude ||
        latitude < mapData.extents.bottom_left.latitude ||
        latitude > mapData.extents.top_right.latitude)
    ) {
      clearUserLocationWatch();
      dispatch(setLocationStatus(LocationStatus.OUTOFBOUNDS));
      return;
    }

    setLocationMarker &&
      setLocationMarker({
        latitude,
        longitude,
        bearing,
        altitude,
        accuracy,
        floorId: floor,
      });

    handleUserWithinGeofence &&
      handleUserWithinGeofence({ latitude, longitude });
  };

  const handleUserLocationFailed: PositionErrorCallback = (error) => {
    clearUserLocationWatch();

    if (error.code === error.PERMISSION_DENIED) {
      onPermissionDenied?.();
    }

    dispatch(setLocationStatus(LocationStatus.DISABLED));
  };

  return {
    handleLocationRequest,
  };
}
